import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Organization, OrganizationMember, OrganizationInvitation } from '../api/organizationApi';

export interface OrganizationState {
  currentOrganization: Organization | null;
  members: OrganizationMember[];
  invitations: OrganizationInvitation[];
  isLoading: boolean;
  error: string | null;
}

const ORG_STORAGE_KEY = 'current_organization_v1';

const loadPersistedOrganization = (): Organization | null => {
  try {
    const stored = localStorage.getItem(ORG_STORAGE_KEY);
    if (stored) return JSON.parse(stored) as Organization;
  } catch { /* ignore */ }
  return null;
};

const persistOrganization = (org: Organization | null) => {
  try {
    if (org) {
      localStorage.setItem(ORG_STORAGE_KEY, JSON.stringify(org));
    } else {
      localStorage.removeItem(ORG_STORAGE_KEY);
    }
  } catch { /* ignore */ }
};

const initialState: OrganizationState = {
  currentOrganization: loadPersistedOrganization(),
  members: [],
  invitations: [],
  isLoading: false,
  error: null,
};

const organizationSlice = createSlice({
  name: 'organization',
  initialState,
  reducers: {
    setCurrentOrganization: (state, action: PayloadAction<Organization | null>) => {
      state.currentOrganization = action.payload;
      persistOrganization(action.payload);
    },
    setMembers: (state, action: PayloadAction<OrganizationMember[]>) => {
      state.members = action.payload;
    },
    addMember: (state, action: PayloadAction<OrganizationMember>) => {
      const existingIndex = state.members.findIndex((m) => m.id === action.payload.id);
      if (existingIndex >= 0) {
        state.members[existingIndex] = action.payload;
      } else {
        state.members.push(action.payload);
      }
    },
    updateMember: (state, action: PayloadAction<OrganizationMember>) => {
      const index = state.members.findIndex((m) => m.id === action.payload.id);
      if (index >= 0) {
        state.members[index] = action.payload;
      }
    },
    removeMember: (state, action: PayloadAction<string>) => {
      state.members = state.members.filter((m) => m.id !== action.payload);
    },
    setInvitations: (state, action: PayloadAction<OrganizationInvitation[]>) => {
      state.invitations = action.payload;
    },
    addInvitation: (state, action: PayloadAction<OrganizationInvitation>) => {
      const existingIndex = state.invitations.findIndex((i) => i.id === action.payload.id);
      if (existingIndex >= 0) {
        state.invitations[existingIndex] = action.payload;
      } else {
        state.invitations.push(action.payload);
      }
    },
    removeInvitation: (state, action: PayloadAction<string>) => {
      state.invitations = state.invitations.filter((i) => i.id !== action.payload);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetOrganization: (state) => {
      state.currentOrganization = null;
      state.members = [];
      state.invitations = [];
      state.error = null;
      persistOrganization(null);
    },
  },
});

export const {
  setCurrentOrganization,
  setMembers,
  addMember,
  updateMember,
  removeMember,
  setInvitations,
  addInvitation,
  removeInvitation,
  setLoading,
  setError,
  clearError,
  resetOrganization,
} = organizationSlice.actions;

export default organizationSlice.reducer;

